import { useMemo } from "react";
import { ArrowDown, Flame } from "lucide-react";
import { BRAND_LOGOS, IMAGES, COMPANY } from "@/lib/site";

function Embers({ count = 22 }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map(() => ({
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 8 + Math.random() * 8,
        drift: (Math.random() - 0.5) * 80,
        size: 2 + Math.random() * 3,
      })),
    [count]
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((p, i) => (
        <span
          key={i}
          className="ember-particle"
          style={{
            left: `${p.left}%`,
            bottom: `-10px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            "--drift": `${p.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
      />
      <div className="absolute inset-0 bg-[#0B1120]/75" />
      <div className="absolute inset-0 hero-vignette" />
      <div className="absolute inset-0 bg-grain opacity-30" />
      <Embers count={28} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-orange-500" />
              <span className="font-tech text-orange-400 text-xs">
                MASTER READY SERVICES, LLC
              </span>
              <Flame size={14} className="text-orange-500" />
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight"
            >
              <span className="chrome-text">More Leads.</span>
              <br />
              <span className="chrome-text">More Appointments.</span>
              <br />
              <span className="ember-text">More Growth.</span>
            </h1>

            <p className="mt-7 text-slate-300 text-base md:text-lg max-w-xl font-body leading-relaxed">
              A parent organization of three specialized lead-generation divisions —
              built for contractors, roofers, solar pros, storm restoration teams, and
              growing home-service businesses. We don't sell leads. We build pipelines.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToId("request")}
                data-testid="hero-cta-primary"
                className="btn-forge"
              >
                Request a Custom Campaign
                <ArrowDown size={16} />
              </button>
              <button
                onClick={() => scrollToId("brands")}
                data-testid="hero-cta-secondary"
                className="btn-chrome"
              >
                Explore Our Brands
              </button>
            </div>

            {/* Stat strip */}
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { v: "3", l: "Brand Divisions" },
                { v: "16+", l: "Service Verticals" },
                { v: "20+", l: "States Covered" },
              ].map((s) => (
                <div key={s.l} className="border-l border-orange-500/30 pl-4">
                  <div className="font-display text-3xl md:text-4xl gold-text">
                    {s.v}
                  </div>
                  <div className="font-tech text-[10px] text-slate-400 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: hero logo */}
          <div className="lg:col-span-5 reveal" style={{ animationDelay: "200ms" }}>
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-orange-500/20 via-transparent to-amber-500/10 blur-3xl" />
              <div className="relative tracing-beam p-2">
                <img
                  src={BRAND_LOGOS.mastersHero}
                  alt="Masters Ready Services"
                  className="relative w-full h-auto rounded-sm"
                  data-testid="hero-logo-image"
                />
              </div>
              <div className="mt-4 text-center font-tech text-[11px] text-slate-400">
                {COMPANY.domain}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => scrollToId("about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate-500 hover:text-orange-400 transition-colors"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </button>
    </section>
  );
}
